/**
 * Boot milestones recorded with the User Timing API.
 *
 * Why not OTel spans: the UI tracing provider is not initialised during boot, so
 * it would miss most of the window we need to measure. `performance.mark`/`measure` is always
 * available, costs nothing, shows up in the Timings track of any Chrome
 * performance recording, and can be read back in the console with
 * `performance.getEntriesByType('measure')`.
 *
 * A startup trace showed ~800ms of client-side gating before the visible page
 * could even request its own data, none of which was instrumented. These marks
 * make that window measurable so regressions are visible without hand-reading a
 * 40MB trace.
 */
export const BootMark = {
  CsrfFetchStart: 'evisort:csrf-fetch:start',
  CsrfFetchEnd: 'evisort:csrf-fetch:end',
  FirstLdIdentifyEnd: 'evisort:ld-identify:end',
  SplashGateOpen: 'evisort:splash-gate:open',
} as const;

type BootMarkName = (typeof BootMark)[keyof typeof BootMark];

function canMeasure() {
  return typeof performance !== 'undefined' && !!performance.mark;
}

/** Records a boot milestone. No-op where the User Timing API is unavailable. */
export function markBoot(name: BootMarkName) {
  if (!canMeasure()) return;
  try {
    performance.mark(name);
  } catch {
    // Never let instrumentation break boot.
  }
}

/**
 * Records a milestone and, when `from` was marked, the duration between them as
 * a named measure. Safe to call more than once — repeat calls are ignored so
 * "first X" milestones stay first.
 */
export function markBootOnce(name: BootMarkName, from?: BootMarkName) {
  if (!canMeasure()) return;
  try {
    if (performance.getEntriesByName(name, 'mark').length > 0) return;
    performance.mark(name);
    if (from && performance.getEntriesByName(from, 'mark').length > 0) {
      performance.measure(name.replace(/:end$/, ':duration'), from, name);
    }
  } catch {
    // Never let instrumentation break boot.
  }
}
